// Shared helper for safely parsing fetch responses, handling non-JSON errors
// (502/504 HTML pages from the load balancer).
//
// Promoted from apps/admin-web/src/lib/parse-response.ts so the registry's
// `createApiClient` (in @auctogon/core) can depend on it without a package →
// app import. The admin path re-exports this for back-compat.

/**
 * The `error` text `parseResponse` synthesizes for a failed response that
 * carried no server message of its own (a non-JSON 502/504 page, or a JSON body
 * with no `error`). Exported so a caller can tell that apart from a
 * server-authored message and show its own fallback copy instead (BL-2069).
 *
 * @param status - The response's HTTP status.
 * @returns `Request failed with status <status>`.
 */
export function requestFailedMessage(status: number): string {
  return `Request failed with status ${status}`;
}

/**
 * Safely parse a fetch Response.
 *
 * For 2xx responses, returns the parsed JSON.
 *
 * For non-2xx responses with a JSON body, returns it **with `success: false`
 * and a normalized `error` field**, preserving any extra fields the route
 * returned (e.g. `code`, `reserve`, `warning`, `fieldErrors`) — callers that
 * branch on `result.code === 'RESERVE_NOT_MET'` rely on this. For non-JSON
 * error bodies, returns `{ success: false, error: "Request failed with status N" }`.
 *
 * @param res - The fetch Response to parse.
 * @returns The parsed JSON body (success path) or a `{ success: false, error, ...rest }` envelope (failure).
 */
export async function parseResponse<T = { success: boolean; error?: string }>(
  res: Response,
): Promise<T> {
  if (!res.ok) {
    const text = await res.text();
    try {
      const parsed = JSON.parse(text);
      // Spread parsed first so extra fields survive, then force `success: false`
      // since the HTTP status was non-2xx, and normalize `error` to a string.
      return {
        ...parsed,
        success: false,
        error: parsed.error || requestFailedMessage(res.status),
      } as T;
    } catch {
      return { success: false, error: requestFailedMessage(res.status) } as T;
    }
  }
  // `Response.json()` is typed `Promise<unknown>` under node/undici libs (it was
  // `any` under the DOM lib in the app); the caller chooses `T` per call.
  return res.json() as Promise<T>;
}
